import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import debounce from 'lodash.debounce';
import playLocalDTMFTone from '../voip/dtmfTone';
import { useVoip } from '../voip/VoipProvider';
import { useGetMembersWithExtensionsQuery } from '../services/VoipService';
import { getSearchUrl } from '../helpers/SearchHelpers';
import { formatPhone } from '../helpers/VoipHelpers';
import { formatTypes } from '../helpers/UserHelpers';
import '../assets/styles/rhinovoip-dialpad.less';

import backspaceButton from '../assets/images/voip/backspace-button.svg';
import callButton from '../assets/images/voip/call-button.svg';
import hangUpButton from '../assets/images/voip/hangup-button.svg';
import micMuteIcon from '../assets/images/voip/mic-mute.svg';
import pauseIcon from '../assets/images/voip/pause.svg';
import rejectButton from '../assets/images/voip/reject-call-button.svg';
import answerButton from '../assets/images/voip/answer-call-button.svg';
import blindTransferIcon from '../assets/images/voip/blind_transfer.png';
import warmTransferIcon from '../assets/images/voip/warm_transfer.png';

const DIGITS = [
  { key: '1', letters: '' },
  { key: '2', letters: 'ABC' },
  { key: '3', letters: 'DEF' },
  { key: '4', letters: 'GHI' },
  { key: '5', letters: 'JKL' },
  { key: '6', letters: 'MNO' },
  { key: '7', letters: 'PQRS' },
  { key: '8', letters: 'TUV' },
  { key: '9', letters: 'WXYZ' },
  { key: '*', letters: '' },
  { key: '0', letters: '+' },
  { key: '#', letters: '' },
];

const isSearchQuery = (value) => /[a-zA-Z]/.test(value);

export default function RhinoVOIPDialPad() {
  const {
    dialedNumber,
    call,
    answer,
    reject,
    hangup,
    toggleMute,
    toggleHold,
    sendDTMF,
    isMuted,
    isHeld,
    callPhase,
    blindTransfer,
    startConsultTransfer,
    completeConsultTransfer,
    cancelConsultTransfer,
    isConsulting,
    isRegistered,
    isSilentMode,
    toggleSilentMode,
    isDndMode,
    toggleDndMode,
  } = useVoip();

  const [number, setNumber] = useState('');
  const [blindTransferMode, setBlindTransferMode] = useState(false);
  const [consultTransferMode, setConsultTransferMode] = useState(false);
  const [transferNotice, setTransferNotice] = useState(null);

  // Search state
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const cancelTokenRef = useRef(null);
  const dropdownRef = useRef(null);

  const { data: membersData } = useGetMembersWithExtensionsQuery();
  const members = Array.isArray(membersData?.objects) ? membersData.objects : [];

  const isIdle = callPhase === 'idle' || callPhase === 'ended';
  const isActive = callPhase === 'active';
  const inTransferMode = blindTransferMode || consultTransferMode;

  let lastPress = 0;

  const clearSearchResults = useCallback(() => {
    setSearchResults([]);
    setExpandedUserId(null);
    setSearchLoading(false);
    if (cancelTokenRef.current) cancelTokenRef.current();
  }, []);

  useEffect(() => {
    if (callPhase === 'ended') {
      setNumber('');
      setBlindTransferMode(false);
      setConsultTransferMode(false);
      setTransferNotice(null);
      clearSearchResults();
    }
  }, [callPhase, clearSearchResults]);

  useEffect(() => {
    if (callPhase === 'active' && dialedNumber) {
      setNumber(dialedNumber);
    }
  }, [callPhase, dialedNumber]);

  // Click-outside dismissal
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        clearSearchResults();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clearSearchResults]);

  // Global search API call (idle mode)
  const performSearch = useCallback((searchText) => {
    if (cancelTokenRef.current) cancelTokenRef.current();

    const url = getSearchUrl({
      search: searchText,
      scope: 'global',
      type: 'name',
      page: 0,
      size: 10,
    });

    setSearchLoading(true);

    axios.get(url, {
      ...(!axios?.defaults?.headers?.common?.Authorization && {
        headers: { Authorization: 'legacy' },
      }),
      cancelToken: new axios.CancelToken((cancel) => {
        cancelTokenRef.current = cancel;
      }),
    })
      .then((response) => {
        setSearchResults(response.data.users || []);
        setSearchLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setSearchResults([]);
          setSearchLoading(false);
        }
      });
  }, []);

  const debouncedSearch = useMemo(() => debounce(performSearch, 300), [performSearch]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Transfer mode: filter members client-side
  const filterMembers = useCallback((searchText) => {
    const lower = searchText.toLowerCase();
    const filtered = members.filter((m) => m.name && m.name.toLowerCase().includes(lower));
    setSearchResults(filtered);
    setSearchLoading(false);
  }, [members]);

  const debouncedMemberSearch = useMemo(() => debounce(filterMembers, 150), [filterMembers]);

  useEffect(() => () => debouncedMemberSearch.cancel(), [debouncedMemberSearch]);

  // Trigger search based on mode
  const triggerSearch = useCallback((value) => {
    if (!isSearchQuery(value) || value.length < 3) {
      clearSearchResults();
      return;
    }

    setSearchLoading(true);
    if (inTransferMode) {
      debouncedMemberSearch(value);
    } else {
      debouncedSearch(value);
    }
  }, [inTransferMode, debouncedSearch, debouncedMemberSearch, clearSearchResults]);

  const handleDigit = (digit) => {
    const now = Date.now();
    if (now - lastPress < 100) return;
    lastPress = now;

    playLocalDTMFTone(digit);

    if (isActive && !inTransferMode && !isConsulting) {
      sendDTMF(digit);
    } else {
      if (searchResults.length > 0) {
        clearSearchResults();
        setNumber(digit);
        return;
      }
      setNumber((prev) => prev + digit);
    }
  };

  const handleBackspace = () => {
    setNumber((prev) => {
      const next = prev.slice(0, -1);
      if (isSearchQuery(next) && next.length >= 3) {
        triggerSearch(next);
      } else {
        clearSearchResults();
      }
      return next;
    });
  };

  const handleClear = () => {
    setNumber('');
    clearSearchResults();
  };

  const handleHangupClick = () => {
    hangup();
    handleClear();
    setBlindTransferMode(false);
    setConsultTransferMode(false);
  };

  const showTransferNotice = () => {
    setTransferNotice('Transfer sent');
    setTimeout(() => setTransferNotice(null), 2000);
  };

  const handlePhoneSelect = (phoneValue) => {
    const dialableNumber = phoneValue.replace(/[^\d+]/g, '');
    setNumber(dialableNumber);
    clearSearchResults();
  };

  /* ---------- Blind transfer ---------- */

  const confirmBlindTransfer = () => {
    if (!number.trim()) return;
    blindTransfer(number.trim());
    setBlindTransferMode(false);
    setNumber('');
    showTransferNotice();
  };

  /* ---------- Consult transfer ---------- */

  const startConsult = () => {
    if (!number.trim()) return;
    startConsultTransfer(number.trim());
    setNumber('');
  };

  const cancelConsult = () => {
    cancelConsultTransfer();
    setConsultTransferMode(false);
    setNumber('');
    clearSearchResults();
  };

  const completeConsult = () => {
    completeConsultTransfer();
    setConsultTransferMode(false);
    setNumber('');
    showTransferNotice();
  };

  // Input onChange
  const handleInputChange = (e) => {
    const raw = e.target.value;

    if (isIdle || inTransferMode) {
      setNumber(raw);
      triggerSearch(raw);
    } else {
      const cleaned = raw.replace(/[^\d*#+]/g, '');
      setNumber(cleaned);
    }
  };

  let dialpadPlaceholder = 'Search or Dial Number';
  if (blindTransferMode) {
    dialpadPlaceholder = 'Search or Transfer To...';
  } else if (consultTransferMode) {
    dialpadPlaceholder = 'Search or Consult Transfer To...';
  }

  const showDropdown = (isIdle || inTransferMode)
    && isSearchQuery(number)
    && (searchResults.length > 0 || searchLoading);

  return (
    <div className="dialpad-container">

      <div className="dialpad-status-bar">
        {/* Phone Status — DND wins so the indicator reflects the user's choice
            even while the SIP unregister round-trip is still in flight. */}
        {(() => {
          let statusClass = 'is-disconnected';
          let statusTitle = 'Disconnected';
          if (isDndMode) {
            statusClass = 'is-dnd';
            statusTitle = 'Do Not Disturb';
          } else if (isRegistered) {
            statusClass = 'is-connected';
            statusTitle = 'Registered';
          }
          return (
            <button
              type="button"
              className={`dialpad-status-btn ${statusClass}`}
              title={statusTitle}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </button>
          );
        })()}

        {/* Ringer */}
        <button
          type="button"
          className={`dialpad-status-btn ${isSilentMode ? 'is-silent' : ''}`}
          onClick={toggleSilentMode}
          title={isSilentMode ? 'Silent' : 'Ringer'}
        >
          {isSilentMode ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
              <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
              <path d="M18 8a6 6 0 0 0-9.33-5" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          )}
        </button>

        {/* Do Not Disturb */}
        <button
          type="button"
          className={`dialpad-status-btn ${isDndMode ? 'is-dnd' : ''}`}
          onClick={toggleDndMode}
          title={isDndMode ? 'Do Not Disturb' : 'Available'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        </button>
      </div>

      {transferNotice && (
        <div className="transfer-notice">
          {transferNotice}
        </div>
      )}

      <div className="dialpad-display" style={{ position: 'relative' }}>
        <input
          value={number}
          placeholder={dialpadPlaceholder}
          onChange={handleInputChange}
          inputMode={(isIdle || inTransferMode) ? 'text' : 'tel'}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          style={{ paddingRight: number ? 28 : undefined }}
        />
        {number && (
          <button
            type="button"
            onClick={() => setNumber('')}
            title="Clear"
            aria-label="Clear input"
            style={{
              position: 'absolute',
              right: 8,
              // The parent .dialpad-display includes the input's 15px
              // margin-bottom in its height, so 50% lands below the input's
              // actual center. Offset up by half the margin to align with
              // the input vertically.
              top: 'calc(50% - 7.5px)',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              fontSize: 16,
              lineHeight: 1,
              color: '#666',
            }}
          >
            ✕
          </button>
        )}
        {showDropdown && (
          <div className="dialpad-search-dropdown" ref={dropdownRef}>
            {searchLoading && searchResults.length === 0 && (
              <div className="dialpad-search-loading">Searching...</div>
            )}
            {inTransferMode ? (
              // Transfer mode: show members with extensions
              searchResults.map((member) => (
                <button
                  key={member.ext}
                  type="button"
                  className="dialpad-search-result-name"
                  onClick={() => handlePhoneSelect(member.ext)}
                >
                  <span>{member.name}</span>
                  <span className="dialpad-search-ext">Ext {member.ext}</span>
                </button>
              ))
            ) : (
              // Idle mode: show contacts with expandable phone lists
              searchResults.map((user) => {
                const userType = formatTypes(user);
                const dob = user.birthday ? moment(user.birthday).format('MM/DD/YYYY') : null;
                return (
                  <div key={user.id} className="dialpad-search-result">
                    <button
                      type="button"
                      className="dialpad-search-result-name"
                      onClick={() => setExpandedUserId(
                        expandedUserId === user.id ? null : user.id,
                      )}
                    >
                      <span className="dialpad-search-result-info">
                        <span className="dialpad-search-result-title">{user.firstName} {user.lastName}</span>
                        {(dob || userType) && (
                          <span className="dialpad-search-result-meta">
                            {dob}
                            {dob && userType && <span className="dialpad-search-result-meta-sep"> &middot; </span>}
                            {userType}
                          </span>
                        )}
                      </span>
                      <span className="dialpad-search-chevron">
                        {expandedUserId === user.id ? '\u25B2' : '\u25BC'}
                      </span>
                    </button>
                    {expandedUserId === user.id && (
                      <div className="dialpad-search-phones">
                        {user.phones && user.phones.length > 0 ? (
                          user.phones.map((phone) => (
                            <button
                              key={phone.id}
                              type="button"
                              className="dialpad-phone-option"
                              onClick={() => handlePhoneSelect(phone.value)}
                            >
                              {formatPhone(phone.value)}
                            </button>
                          ))
                        ) : (
                          <div className="dialpad-no-phones">No phone numbers</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      <div className="dialpad-grid">
        {DIGITS.map(({ key, letters }) => (
          <button
            key={key}
            type="button"
            className="digit-button"
            onClick={() => handleDigit(key)}
          >
            <div className="digit-text">{key}</div>
            {letters && <div className="letters">{letters}</div>}
          </button>
        ))}
      </div>

      <div className="dial-pad-controls">

        {/* Idle */}
        {isIdle && (
          <div className="call-controls">
            <button type="button" className="call-button" onClick={() => call(number)}>
              <img src={callButton} alt="Call" draggable="false" />
            </button>

            <button
              type="button"
              className={`backspace-button ${number.length ? 'visible' : 'hidden'}`}
              onClick={handleBackspace}
            >
              <img src={backspaceButton} alt="Backspace" draggable="false" />
            </button>
          </div>
        )}

        {/* Incoming */}
        {callPhase === 'ringing' && (
          <div className="incoming-controls">
            <button type="button" className="answer-button" onClick={answer}>
              <img src={answerButton} alt="Answer" draggable="false" />
            </button>
            <button type="button" className="reject-button" onClick={reject}>
              <img src={rejectButton} alt="Reject" draggable="false" />
            </button>
          </div>
        )}

        {/* Active – normal */}
        {isActive && !blindTransferMode && !consultTransferMode && !isConsulting && (
          <div className="active-controls grid-two-tier">

            <button type="button" className={`mute-button ${isMuted ? 'active' : ''}`} onClick={toggleMute}>
              <img src={micMuteIcon} alt="Mute" draggable="false" />
            </button>

            <button type="button" className="hangup-button" onClick={handleHangupClick}>
              <img src={hangUpButton} alt="Hang Up" draggable="false" />
            </button>

            <button type="button" className={`hold-button ${isHeld ? 'active' : ''}`} onClick={toggleHold}>
              <img src={pauseIcon} alt="Hold" draggable="false" />
            </button>

            <button
              type="button"
              className="transfer-button blind"
              onClick={() => {
                setNumber('');
                setBlindTransferMode(true);
                clearSearchResults();
              }}
              title="Cold transfer — send the call immediately, no conversation"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <img src={blindTransferIcon} alt="Cold transfer" width="48" height="32" draggable="false" />
              <span style={{ fontSize: 11, fontWeight: 500, color: 'inherit' }}>Cold</span>
            </button>

            <button
              type="button"
              className="transfer-button consult"
              onClick={() => {
                setNumber('');
                setConsultTransferMode(true);
                clearSearchResults();
              }}
              title="Warm transfer — speak to the recipient first, then connect"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <img src={warmTransferIcon} alt="Warm transfer" width="48" height="32" draggable="false" />
              <span style={{ fontSize: 11, fontWeight: 500, color: 'inherit' }}>Warm</span>
            </button>

          </div>
        )}

        {/* Blind transfer */}
        {isActive && blindTransferMode && (
          <div className="transfer-controls">
            <button type="button" className="cancel-transfer-button" onClick={() => { setBlindTransferMode(false); clearSearchResults(); }}>
              Cancel
            </button>
            <button
              type="button"
              className="confirm-transfer-button"
              disabled={!number.trim()}
              onClick={confirmBlindTransfer}
            >
              Transfer
            </button>
          </div>
        )}

        {/* Consult dialing */}
        {isActive && consultTransferMode && !isConsulting && (
          <div className="transfer-controls">
            <button type="button" className="cancel-transfer-button" onClick={cancelConsult}>
              Cancel
            </button>
            <button
              type="button"
              className="confirm-transfer-button"
              disabled={!number.trim()}
              onClick={startConsult}
            >
              Call
            </button>
          </div>
        )}

        {/* Consult active */}
        {isActive && isConsulting && (
          <div className="transfer-controls">
            <button type="button" className="cancel-transfer-button" onClick={cancelConsult}>
              Cancel
            </button>
            <button
              type="button"
              className="confirm-transfer-button"
              onClick={completeConsult}
            >
              Complete Transfer
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
